
import React from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, Sector } from "recharts";

// Enhanced color scheme with better contrast
export const COLORS = [
  '#0072BC', // Primary blue
  '#FF9500', // Orange
  '#34C759', // Green
  '#5856D6', // Purple
  '#FF3B30', // Red
  '#FFCC00', // Yellow
  '#004880', // Dark blue
];

// Improved custom tooltip for charts
export const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-md shadow-md">
        <p className="font-medium text-sm">{`${payload[0].name || label}`}</p>
        <p className="text-sm text-primary">{`Aantal: ${payload[0].value}`}</p>
        {payload[0].payload.percentage && 
          <p className="text-sm text-gray-600">{`Percentage: ${payload[0].payload.percentage}%`}</p>
        }
      </div>
    );
  }
  return null;
};

// Improved active sector rendering
const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6} // Slightly larger to show active selection
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke="#fff"
        strokeWidth={2}
        className="filter drop-shadow-md"
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        strokeOpacity={0.5}
      />
    </g>
  );
};

interface PieChartDisplayProps {
  data: Array<{
    name: string;
    value: number;
    percentage?: number;
  }>;
  height?: number | string;
  showLabel?: boolean;
  labelLines?: boolean;
  fontSize?: number;
}

const PieChartDisplay: React.FC<PieChartDisplayProps> = ({ 
  data, 
  height = "100%",
  showLabel = true,
  labelLines = false,
  fontSize = 12
}) => {
  const [activeIndex, setActiveIndex] = React.useState<number | undefined>(undefined);

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(undefined);
  };

  // Ensure data array is not empty to avoid rendering errors
  if (!data || data.length === 0) {
    return <div className="h-80 w-full flex items-center justify-center text-gray-500">Geen gegevens beschikbaar</div>;
  }

  // Create labels with more space for readability
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 15; // Position labels further from the pie
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill={COLORS[index % COLORS.length]}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight="500"
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart margin={{ top: 20, right: 30, bottom: 30, left: 30 }}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={labelLines}
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            outerRadius={80}
            innerRadius={0}
            fill="#8884d8"
            dataKey="value"
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
            animationDuration={800}
            animationEasing="ease-out"
            label={showLabel ? renderCustomizedLabel : undefined}
            className="drop-shadow-sm hover:drop-shadow-md transition-all duration-300"
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]} 
                stroke="#fff" 
                strokeWidth={1}
              />
            ))}
          </Pie>
          <Tooltip 
            content={<CustomTooltip />}
            wrapperStyle={{ 
              backgroundColor: 'white', 
              border: '1px solid #ccc', 
              borderRadius: '6px',
              boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1)',
              zIndex: 100 
            }}
          />
          <Legend 
            layout="horizontal" 
            verticalAlign="bottom" 
            align="center"
            formatter={(value) => <span className="text-sm font-medium">{value}</span>}
            iconType="circle"
            iconSize={10}
            wrapperStyle={{ paddingTop: '20px' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChartDisplay;
